import { Component, EventEmitter, Output, signal } from '@angular/core';
import { Client, JourneyDistrictReportData, JourneyDistrictSchoolData } from '@mira-avant/mtlibs';
import { JourneyDistrictReportSummary } from './journey-district-report-summary/journey-district-report-summary';
import { Column, ReportingTable } from '../../reporting-table/reporting-table';
import { HeaderColumn } from '../../reporting-table/header-column';
import { RowColumn } from '../../reporting-table/row-column';
import { ClientService } from '../../../../../services/client.service';
import { CommonModule } from '@angular/common';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NavigationService } from '../../../../../services/navigation.service';
import { firstValueFrom, Subscription } from 'rxjs';
import { BrowserService } from '../../../../../services/browser.service';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { JourneyReports } from '../../../../../services/journey-reports';

const FULL_WIDTH_BREAKPOINT = 1100;

@Component({
	selector: 'app-journey-district-report',
	imports: [
		CommonModule,
		JourneyDistrictReportSummary,
		ReportingTable,
		RowColumn,
		HeaderColumn,
		NzIconModule,
		NzTooltipModule,
	],
	standalone: true,
	templateUrl: './journey-district-report.html',
	styleUrl: './journey-district-report.scss',
})
export class JourneyDistrictReport {
	@Output('onSelectLocation') public onSelectLocation = new EventEmitter<string>();

	public report = signal<JourneyDistrictReportData | undefined>(undefined);
	public loading = signal<boolean>(true);
	public rows = signal<JourneyDistrictSchoolData[]>([]);
	public columns = signal<Column[]>([]);
	public client = signal<Client | undefined>(undefined);
	private sub?: Subscription;
	private wideColumns: Column[] = [];
	private narrowColumns: Column[] = [];

	constructor(
		private journeyReportsService: JourneyReports,
		private clientService: ClientService,
		private nav: NavigationService,
		private browserService: BrowserService
	) {}

	ngOnInit() {
		this.reload();
		this.clientService.getClientBySubDomain$().then(client => {
			this.client.set(client);
		});
	}

	public async reload(): Promise<void> {
		this.loading.set(true);

		try {
			const params = await firstValueFrom(this.nav.matrixParams$);

			if (!params['product']) {
				throw new Error('Product parameter is missing');
			}

			const reportData = await this.journeyReportsService.getJourneyDistrictReport$(params['product']);
			this.report.set(reportData);
			this.rows.set(reportData.schools);

			this.updateTableDetails();

			this.sub?.unsubscribe();
			this.sub = this.browserService.onResize$.subscribe((width: number) => {
				if (width >= FULL_WIDTH_BREAKPOINT) {
					this.columns.set(this.wideColumns);
				} else {
					this.columns.set(this.narrowColumns);
				}
			});
		} catch (error) {
			console.error('Error fetching district report:', error);
		} finally {
			this.loading.set(false);
		}
	}

	private updateTableDetails(): void {
		const productNameColumn: Column = {
			key: 'productName',
			name: 'Product',
			sortOrder: 'ascend',
			sortFn: (a: JourneyDistrictSchoolData, b: JourneyDistrictSchoolData) =>
				a.productName.localeCompare(b.productName),
			sortDirections: ['ascend', 'descend', null],
			filterMultiple: true,
			filterOptions: this.rows().map(row => ({ text: row.productName, value: row.productName })),
			filterFn: (list: string[], item: JourneyDistrictSchoolData) =>
				list.some(name => item.productName.indexOf(name) !== -1),
		};

		const schoolColumn: Column = {
			key: 'name',
			name: 'School',
			sortOrder: 'ascend',
			sortFn: (a: JourneyDistrictSchoolData, b: JourneyDistrictSchoolData) => a.name.localeCompare(b.name),
			sortDirections: ['ascend', 'descend', null],
			filterMultiple: true,
			filterOptions: this.rows().map(row => ({ text: row.name, value: row.name })),
			filterFn: (list: string[], item: JourneyDistrictSchoolData) =>
				list.some(name => item.name.indexOf(name) !== -1),
		};

		const teacherColumn: Column = {
			key: 'teacherCount',
			name: 'Teachers',
			sortOrder: null,
			sortFn: (a: JourneyDistrictSchoolData, b: JourneyDistrictSchoolData) =>
				a.teacherCount.toString().localeCompare(b.teacherCount.toString()),
			sortDirections: ['ascend', 'descend', null],
		};

		const sectionColumn: Column = {
			key: 'sectionCount',
			name: 'Classes',
			sortOrder: null,
			sortFn: (a: JourneyDistrictSchoolData, b: JourneyDistrictSchoolData) =>
				a.sectionCount.toString().localeCompare(b.sectionCount.toString()),
			sortDirections: ['ascend', 'descend', null],
		};

		const studentColumn: Column = {
			key: 'studentCount',
			name: 'Students',
			sortOrder: null,
			sortFn: (a: JourneyDistrictSchoolData, b: JourneyDistrictSchoolData) =>
				a.studentCount.toString().localeCompare(b.studentCount.toString()),
			sortDirections: ['ascend', 'descend', null],
		};

		const activityColumn: Column = {
			key: 'activityCount',
			name: 'Activities',
			sortOrder: null,
			sortFn: (a: JourneyDistrictSchoolData, b: JourneyDistrictSchoolData) =>
				a.activityCount.toString().localeCompare(b.activityCount.toString()),
			sortDirections: ['ascend', 'descend', null],
		};

		const activitiesPerStudentColumn: Column = {
			key: 'activitiesPerStudent',
			name: 'Activities Per Student',
			sortOrder: null,
			sortFn: (a: JourneyDistrictSchoolData, b: JourneyDistrictSchoolData) =>
				a.activitiesPerStudent.toString().localeCompare(b.activitiesPerStudent.toString()),
			sortDirections: ['ascend', 'descend', null],
		};

		this.wideColumns = [
			productNameColumn,
			schoolColumn,
			teacherColumn,
			sectionColumn,
			studentColumn,
			activityColumn,
			activitiesPerStudentColumn,
		];

		this.narrowColumns = [productNameColumn, schoolColumn, activityColumn, activitiesPerStudentColumn];

		this.columns.set(this.wideColumns);
	}

	public async onRowClick(row: JourneyDistrictSchoolData) {
		this.nav.navigateWithMatrix$(['/', 'my', 'reports'], { locationUuid: row.locationUuid });
	}

	ngOnDestroy() {
		this.sub?.unsubscribe();
	}
}
